import { LockIcon, LockOpenIcon, StarIcon } from 'lucide-react';
import {
    Box,
    Card,
    CardBody,
    Heading,
    Text,
    List,
    ListItem,
    ListIcon,
    useColorModeValue,
    Badge,
    HStack,
    Tooltip,
    CardFooter,
} from '@chakra-ui/react';

import { Perk, Service } from '../../../types/service.type';
import { Plan } from '../../../types/plan.type';

import {
    getPlanTypeParsed,
    getPriorityColor,
    getServiceType,
} from './service-card.util';

interface Props {
    service: Service;
    onUpdate: (service: Service) => void;
}

const ServiceCard = ({ service }: Props) => {
    const cardBg = useColorModeValue('white', 'gray.700');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const accentColor = useColorModeValue('blue.500', 'blue.300');

    return (
        <Card
            bg={cardBg}
            border="1px"
            borderColor={borderColor}
            boxShadow="lg"
            borderRadius="xl"
            overflow="hidden"
            position="relative"
            w={'100%'}
        >
            <Box
                position="absolute"
                top={3}
                right={3}
                display={'grid'}
                textAlign={'center'}
                gap={2}
            >
                <Badge
                    colorScheme={getPriorityColor(service.priority)}
                    borderRadius="full"
                    px={2}
                    py={1}
                >
                    Prioridad {service.priority}
                </Badge>
                <Badge
                    borderRadius="full"
                    px={2}
                    py={1}
                    colorScheme="purple"
                    variant="subtle"
                >
                    {getServiceType(service.type)}
                </Badge>
            </Box>

            <CardBody>
                <Box display="flex" alignItems="center">
                    <Heading w={'fit-content'} size="md" color={accentColor}>
                        {service.name} ({service.code})
                    </Heading>
                </Box>

                <Text wordBreak={'break-all'} w={'80%'}>
                    Descripcion: {service.description}
                </Text>

                <Text>
                    Servicios de Remolque Gratis:{' '}
                    {service.totalFreeTowingServices}
                </Text>
                <Text>
                    Servicios de Ambulancia Gratis:{' '}
                    {service.totalFreeAmbulanceServices}
                </Text>
                <Text>
                    Días de Elegibilidad de Remolque:{' '}
                    {service.towingEligibilityDays}
                </Text>
                <Perks perks={service.perks} />
            </CardBody>

            <CardFooter>
                <HStack justify="space-between" w="full">
                    <Plans plans={service.plans} />

                    {/* <Button
                        size="sm"
                        colorScheme="primary"
                        display={'flex'}
                        gap={2}
                        onClick={() => onUpdate(service)}
                    >
                        <PenIcon size={15} /> Editar
                    </Button> */}
                </HStack>
            </CardFooter>
        </Card>
    );
};

function Plans({ plans }: { plans: Plan[] | undefined }) {
    if (!plans) {
        return null;
    }

    return (
        <Box>
            <Box display={'flex'} gap={2} flexWrap={'wrap'}>
                {plans.map((plan) => (
                    <Tooltip
                        key={plan.id}
                        label={getPlanTypeParsed(plan.type)}
                        hasArrow
                    >
                        <Badge
                            borderRadius="full"
                            px={2}
                            py={1}
                            colorScheme="gray"
                            variant="subtle"
                            position={'relative'}
                        >
                            <Text fontSize="sm" fontWeight="medium">
                                {plan.description} -{' '}
                                {getPlanTypeParsed(plan.type)}
                            </Text>

                            <Box
                                position={'absolute'}
                                top={-3}
                                right={-2}
                                color={plan.active ? 'green.500' : 'red.500'}
                            >
                                {plan.active ? <LockOpenIcon /> : <LockIcon />}
                            </Box>
                        </Badge>
                    </Tooltip>
                ))}
            </Box>
        </Box>
    );
}

function Perks({ perks }: { perks: Perk[] | undefined }) {
    if (!perks) {
        return null;
    }

    return (
        <Box>
            <Text fontWeight="bold" mb={2} fontSize="sm" color="gray.700">
                Beneficios:
            </Text>
            <List spacing={2}>
                {perks.map((perk) => (
                    <ListItem key={perk.id} display="flex" alignItems="center">
                        <ListIcon as={StarIcon} color="yellow.500" />
                        <Text fontSize="sm">{perk.name}</Text>
                    </ListItem>
                ))}
            </List>
        </Box>
    );
}

export default ServiceCard;
